/**
 * Contact Us Form Handler for /pages/contact
 * Handles the specific contact form on the contact us page
 */

document.addEventListener('DOMContentLoaded', function () {
  const netlifyFunctionUrl = 'https://orvizoho.netlify.app/.netlify/functions/contact-us';

  // Global submission tracking
  const submissionTracker = {
    activeSubmissions: new Set(),
    isSubmitting: function (formId) {
      return this.activeSubmissions.has(formId);
    },
    startSubmission: function (formId) {
      this.activeSubmissions.add(formId);
    },
    endSubmission: function (formId) {
      this.activeSubmissions.delete(formId);
    }
  };

  function initContactUsForm() {
    // Handle BOTH contact forms (since there are 2 with same ID)
    const allContactForms = [
      ...document.querySelectorAll('form[action="/contact#contact_contact_form"]'),
      ...document.querySelectorAll('form[action="/contact#ContactForm"]'),
      ...document.querySelectorAll('form#ContactForm'),
      ...document.querySelectorAll('.contact-form form')
    ];

    // Remove duplicates based on form content/structure
    const uniqueForms = [];
    allContactForms.forEach(form => {
      if (!uniqueForms.find(existing => existing.isEqualNode(form))) {
        uniqueForms.push(form);
      }
    });

    console.log(`Found ${uniqueForms.length} unique contact forms to handle`);

    uniqueForms.forEach((contactForm, index) => {
      // Check if form is already handled by any script
      if (contactForm.hasAttribute('data-contact-us-handled') ||
        contactForm.hasAttribute('data-orvi-handled') ||
        contactForm.hasAttribute('data-handled')) {
        console.log(`Form ${index} already handled, skipping`);
        return;
      }

      // Exclude forms that should be handled by hometellus script
      // Check if this form has hometellus/project-related fields or content
      const hasProjectFields = contactForm.querySelector('input[name="contact[Company]"], textarea[name="contact[Message]"]');
      const formText = contactForm.textContent.toLowerCase();
      const isProjectForm = formText.includes('tell us about') || formText.includes('project') ||
        formText.includes('hometellus') || hasProjectFields;

      if (isProjectForm) {
        console.log(`Form ${index} appears to be a project/hometellus form, skipping for contact-us script`);
        return;
      }

      console.log(`Initializing contact form ${index}:`, {
        id: contactForm.id,
        action: contactForm.action,
        className: contactForm.className
      });

      setupSingleContactForm(contactForm, index);
    });
  }

  function setupSingleContactForm(contactForm, formIndex) {
    // Inject City field if missing (User Request: Add City Under the email)
    const emailInput = contactForm.querySelector('input[name="contact[email]"]');
    const cityInputExists = contactForm.querySelector('input[name="contact[city]"], input[name="contact[City]"]');

    if (emailInput && !cityInputExists) {
      console.log('Injecting City field into Contact Us form');
      const cityWrapper = document.createElement('div');
      cityWrapper.className = 'field'; // Common Shopify class
      // Try to match the class of the email input's container
      if (emailInput.parentElement.classList.contains('field') || emailInput.parentElement.classList.contains('grid__item')) {
        cityWrapper.className = emailInput.parentElement.className;
      }

      cityWrapper.innerHTML = `
        <input type="text" 
               id="ContactFormCity-${formIndex}" 
               name="contact[city]" 
               placeholder="City" 
               class="${emailInput.className}" 
               aria-label="City"
               required>
        <label class="field__label" for="ContactFormCity-${formIndex}" style="${emailInput.nextElementSibling?.tagName === 'LABEL' ? '' : 'display:none'}">City</label>
      `;

      // Insert after the email input's parent (if wrapped) or after the email input itself
      if (emailInput.closest('.field') || emailInput.closest('.grid__item') || emailInput.closest('.input-group')) {
        emailInput.closest('.field, .grid__item, .input-group').after(cityWrapper);
      } else {
        emailInput.after(cityWrapper);
      }
    }

    // Generate unique form ID for tracking (make it truly unique)
    const formId = contactForm.id ? `${contactForm.id}-${formIndex}` : `contact-form-${formIndex}-${Date.now()}`;

    // Don't change the actual form ID as it might break other scripts
    // contactForm.id = formId;

    // Find form fields
    const fieldMappings = {
      firstName: contactForm.querySelector('input[name="contact[first_name]"]'),
      lastName: contactForm.querySelector('input[name="contact[last_name]"]'),
      email: contactForm.querySelector('input[name="contact[email]"]'),
      phone: contactForm.querySelector('input[name="contact[phone]"]'),
      city: contactForm.querySelector('input[name="contact[City]"]') || contactForm.querySelector('input[name="contact[city]"]'),
      address: contactForm.querySelector('input[name="contact[Address]"]'),
      requestType: contactForm.querySelector('select[name="contact[request_type]"]'),
      message: contactForm.querySelector('textarea[name="contact[body]"]'),
      formNameField: contactForm.querySelector('input[name="contact[Form Name]"]')
    };

    // Validate required fields are present (only check core required fields)
    const missingFields = [];
    if (!fieldMappings.firstName) missingFields.push('firstName');
    if (!fieldMappings.lastName) missingFields.push('lastName');
    if (!fieldMappings.email) missingFields.push('email');
    if (!fieldMappings.phone) missingFields.push('phone');
    // City is now mandatory, but if the form doesn't have the field yet, we shouldn't break existing forms if they aren't updated in Liquid. 
    // However, the user says "Add City... Mandatory All Form". I will warn if missing but proceed if not found (user might update liquid later)
    if (!fieldMappings.city) console.warn('City field not found in form');

    if (missingFields.length > 0) {
      console.error('Contact form missing required fields:', missingFields);
      console.log('Available form elements:', contactForm.innerHTML);
      return;
    }

    console.log('Contact form initialized successfully:', {
      formId: contactForm.id,
      action: contactForm.action,
      hasFirstName: !!fieldMappings.firstName,
      hasLastName: !!fieldMappings.lastName,
      hasEmail: !!fieldMappings.email,
      hasPhone: !!fieldMappings.phone
    });

    // Remove any existing event listeners to prevent duplicates
    if (contactForm._orviContactHandler) {
      contactForm.removeEventListener('submit', contactForm._orviContactHandler);
    }

    const submitHandler = async function (e) {
      e.preventDefault();
      e.stopImmediatePropagation();

      // Check if already submitting
      if (submissionTracker.isSubmitting(formId)) {
        return;
      }

      // Start tracking submission
      submissionTracker.startSubmission(formId);

      // Disable form interactions during submission
      this.style.pointerEvents = 'none';

      // Get form data
      // Detect form type: if it's the homepage popup, it will have "Appointments" hidden field
      const isHomepagePopup = fieldMappings.formNameField?.value === 'Appointments';
      console.log('Contact form submission:', {
        formId: this.id,
        action: this.action,
        isHomepagePopup: isHomepagePopup,
        formNameFieldValue: fieldMappings.formNameField?.value
      });

      const formData = {
        firstName: (fieldMappings.firstName?.value || '').trim(),
        lastName: (fieldMappings.lastName?.value || '').trim(),
        email: (fieldMappings.email?.value || '').trim(),
        phone: (fieldMappings.phone?.value || '').trim(),
        city: (fieldMappings.city?.value || '').trim(),
        address: (fieldMappings.address?.value || '').trim(),
        request_type: (fieldMappings.requestType?.value || 'General Inquiry').trim(), // Default value
        body: (fieldMappings.message?.value || (isHomepagePopup ? 'Contact request from homepage appointment form' : '')).trim(),
        formName: isHomepagePopup ? 'Website Schedule A Call' : 'Contact us', // Different formName for homepage popup
        leadSource: isHomepagePopup ? 'Website Schedule A Call' : 'Contact us',
        sourceByGroup: isHomepagePopup ? 'Website Schedule A Call' : 'Contact us'
      };

      console.log('Form field mappings found:', {
        hasFirstName: !!fieldMappings.firstName,
        hasLastName: !!fieldMappings.lastName,
        hasEmail: !!fieldMappings.email,
        hasPhone: !!fieldMappings.phone,
        hasCity: !!fieldMappings.city,
        hasAddress: !!fieldMappings.address,
        hasRequestType: !!fieldMappings.requestType,
        hasMessage: !!fieldMappings.message,
        hasFormNameField: !!fieldMappings.formNameField,
        formNameValue: fieldMappings.formNameField?.value
      });

      // Validate required fields
      const emptyFields = [];
      if (!formData.firstName) emptyFields.push('First Name');
      if (!formData.lastName) emptyFields.push('Last Name');
      if (!formData.email) emptyFields.push('Email');
      if (!formData.phone) emptyFields.push('Phone');
      if (!formData.city) emptyFields.push('City');
      // Message is now optional
      // if (!formData.body) emptyFields.push('Message'); 

      if (emptyFields.length > 0) {
        alert(`Please fill in the following required fields: ${emptyFields.join(', ')}`);
        submissionTracker.endSubmission(formId);
        this.style.pointerEvents = '';
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        alert('Please enter a valid email address.');
        submissionTracker.endSubmission(formId);
        this.style.pointerEvents = '';
        return;
      }

      // Show loading state
      const submitBtn = this.querySelector('button[type="submit"], input[type="submit"]');
      const originalText = submitBtn ? (submitBtn.innerHTML || submitBtn.value) : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        if (submitBtn.tagName === 'BUTTON') {
          submitBtn.innerHTML = 'SUBMITTING...';
        } else {
          submitBtn.value = 'SUBMITTING...';
        }
      }

      console.log('Submitting form data to Netlify:', formData);
      console.log('Netlify function URL:', netlifyFunctionUrl);

      try {
        const response = await fetch(netlifyFunctionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(formData),
        });

        console.log('Response received:', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok
        });

        let result;
        try {
          result = await response.json();
        } catch (parseError) {
          throw new Error('Invalid response from server');
        }

        if (response.ok && result.success) {
          console.log('Form submitted successfully:', result);

          // Show success message
          if (result.duplicate) {
            alert('Your contact information has already been submitted!');
          } else {
            alert('Thank you! Your message has been sent successfully. We will get back to you soon.');
          }

          this.reset();

          // Show success state on button temporarily
          if (submitBtn) {
            if (submitBtn.tagName === 'BUTTON') {
              submitBtn.innerHTML = 'SENT!';
            } else {
              submitBtn.value = 'SENT!';
            }
          }
        } else {
          console.error('Form submission failed:', {
            status: response.status,
            statusText: response.statusText,
            result: result
          });
          alert('There was an error sending your message. Please try again later.');
        }
      } catch (error) {
        console.error('Contact form submission error:', error);
        alert('Network error. Please check your connection and try again.');
      } finally {
        // Always restore form state
        submissionTracker.endSubmission(formId);
        this.style.pointerEvents = '';

        if (submitBtn) {
          setTimeout(() => {
            if (submitBtn.tagName === 'BUTTON') {
              submitBtn.innerHTML = originalText;
            } else {
              submitBtn.value = originalText;
            }
            submitBtn.disabled = false;
          }, 2000);
        }
      }
    };

    // Store handler reference and add event listener
    contactForm._orviContactHandler = submitHandler;
    contactForm.addEventListener('submit', submitHandler);
    contactForm.setAttribute('data-contact-us-handled', 'true');

    console.log(`Contact form ${formIndex} initialized successfully`);
  } // End of setupSingleContactForm function

  // Setup appointment link handler for homepage popup
  const setupAppointmentLinks = () => {
    const appointmentLinks = document.querySelectorAll('a.appointment, .appointment a, a[href*="appointment"], button.appointment');
    appointmentLinks.forEach(link => {
      link.removeEventListener('click', handleAppointmentClick);
      link.addEventListener('click', handleAppointmentClick);
    });
  };

  const handleAppointmentClick = () => {
    // Wait for popup to appear and reinitialize form
    setTimeout(() => {
      initContactUsForm();
    }, 100);
    setTimeout(() => {
      initContactUsForm();
    }, 500);
  };

  // Initialize the form
  initContactUsForm();
  setupAppointmentLinks();

  // Also try again after a delay in case the forms load dynamically
  setTimeout(() => {
    initContactUsForm();
    setupAppointmentLinks();
  }, 1000);

  // Watch for dynamic content changes
  const observer = new MutationObserver(() => {
    setTimeout(() => {
      initContactUsForm();
      setupAppointmentLinks();
    }, 100);
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});